import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Hls from 'hls.js';
import { 
    Video, Image as ImageIcon, Sparkles, Upload, Eye, CheckCircle2, 
    ShieldCheck, DollarSign, Package, Layers, Globe, ArrowRight, 
    Play, Pause, Plus, Trash2, HelpCircle, AlertCircle, RefreshCw,
    Smartphone, Monitor, Tag, Info, Film, UploadCloud, CheckCircle,
    Clock, Cpu, Volume2, VolumeX, X, Sliders, ChevronUp, ChevronDown
} from 'lucide-react';
import { 
    getCategories, 
    getActiveCountries, 
    getVendorApplicationStatus, 
    createProduct,
    uploadMedia,
    getJobStatus,
    resolveStorefrontImageUrl
} from '../api/client';
import { isRTL } from '../utils/translations';

// ── Real-Time HLS Video Player Component ─────────────────────────────
const HlsVideoPlayer = ({ src, poster, autoPlay = true, loop = true, muted = true, style }) => {
    const videoRef = useRef(null);
    const hlsRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(autoPlay);
    const [isMuted, setIsMuted] = useState(muted);
    const [hasHlsLevels, setHasHlsLevels] = useState(false);

    const resolvedSrc = src ? resolveStorefrontImageUrl(src) : '';

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !resolvedSrc) return;

        const isHls = resolvedSrc.includes('.m3u8');

        if (isHls && Hls.isSupported()) {
            if (hlsRef.current) {
                hlsRef.current.destroy();
            }
            const hls = new Hls({
                enableWorker: true,
                startLevel: -1,
                capLevelToPlayerSize: false,
                autoStartLoad: true
            });
            hlsRef.current = hls;

            hls.loadSource(resolvedSrc);
            hls.attachMedia(video);

            hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
                if (data.levels && data.levels.length > 0) {
                    setHasHlsLevels(true);
                }
                if (autoPlay) {
                    video.play().catch(() => {});
                }
            });

            return () => {
                hls.destroy();
                hlsRef.current = null;
            };
        } else if (isHls && video.canPlayType('application/vnd.apple.mpegurl')) {
            video.src = resolvedSrc;
            if (autoPlay) video.play().catch(() => {});
            setHasHlsLevels(true);
        } else {
            video.src = resolvedSrc;
            if (autoPlay) video.play().catch(() => {});
            setHasHlsLevels(false);
        }
    }, [resolvedSrc, autoPlay]);

    const togglePlay = () => {
        if (!videoRef.current) return;
        if (videoRef.current.paused) {
            videoRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
        } else {
            videoRef.current.pause();
            setIsPlaying(false);
        }
    };

    const toggleMute = (e) => {
        e.stopPropagation();
        if (!videoRef.current) return;
        videoRef.current.muted = !videoRef.current.muted;
        setIsMuted(videoRef.current.muted);
    };

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', ...style }} onClick={togglePlay}>
            <video
                ref={videoRef}
                poster={poster ? resolveStorefrontImageUrl(poster) : undefined}
                loop={loop}
                muted={isMuted}
                playsInline
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', cursor: 'pointer' }}
            />
            
            {/* Play/Pause state indicator */}
            {!isPlaying && (
                <div style={{
                    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(0,0,0,0.35)', pointerEvents: 'none'
                }}>
                    <div style={{ background: 'rgba(212,175,55,0.95)', color: '#000', borderRadius: '50%', padding: '14px', boxShadow: '0 4px 20px rgba(0,0,0,0.5)' }}>
                        <Play size={24} fill="#000" />
                    </div>
                </div>
            )}

            {/* Mute toggle button */}
            <button
                type="button"
                onClick={toggleMute}
                style={{
                    position: 'absolute', top: '12px', left: '12px', zIndex: 10,
                    background: 'rgba(0,0,0,0.65)', border: '1px solid rgba(255,255,255,0.2)',
                    color: '#fff', borderRadius: '50%', width: '32px', height: '32px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                    backdropFilter: 'blur(6px)'
                }}
            >
                {isMuted ? <VolumeX size={15} /> : <Volume2 size={15} />}
            </button>

            {/* Adaptive HLS badge */}
            {hasHlsLevels && (
                <div style={{
                    position: 'absolute', bottom: '12px', left: '12px', zIndex: 10,
                    background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(212,175,55,0.6)', color: '#d4af37',
                    padding: '3px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '800',
                    display: 'flex', alignItems: 'center', gap: '5px'
                }}>
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#10b981', boxShadow: '0 0 8px #10b981' }} />
                    <span>HLS 1080p • 720p • 480p</span>
                </div>
            )}
        </div>
    );
};

const VendorPublishProductPage = () => {
    const navigate = useNavigate();
    const rtl = isRTL();

    const [application, setApplication] = useState(null);
    const [categories, setCategories] = useState([]);
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [aiGenerating, setAiGenerating] = useState(false);
    const [previewMode, setPreviewMode] = useState('mobile'); // 'mobile' | 'desktop'

    // Form State
    const [formData, setFormData] = useState({
        name: '',
        nameAr: '',
        description: '',
        descriptionAr: '',
        price: '',
        compareAtPrice: '',
        quantity: 10,
        sku: 'SKU-' + Math.floor(100000 + Math.random() * 900000),
        categoryId: '',
        countryCode: '',
        images: [''],
        videoUrl: '',
        aspectRatio: '9:16', // '9:16' (Reels) | '16:9' (Cinematic)
        attributes: [
            { name: 'الضمان', value: 'أصلي 100% معتمد' }
        ]
    });

    // Video Upload & Transcoding Pipeline State
    const [videoMode, setVideoMode] = useState('UPLOAD'); // 'UPLOAD' | 'URL'
    const [videoFile, setVideoFile] = useState(null);
    const [videoUploading, setVideoUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [jobStatus, setJobStatus] = useState('IDLE'); // 'IDLE' | 'UPLOADING' | 'ANALYZING' | 'CONVERTING' | 'GENERATING_THUMBNAILS' | 'COMPLETED' | 'FAILED'
    const [jobProgress, setJobProgress] = useState(0);
    const [jobStepText, setJobStepText] = useState('');
    const [jobError, setJobError] = useState('');
    const [dragOverVideo, setDragOverVideo] = useState(false);
    const [photoUploading, setPhotoUploading] = useState(false);
    const [photoUploadProgress, setPhotoUploadProgress] = useState(0);
    const [dragOverPhotos, setDragOverPhotos] = useState(false);

    const videoInputRef = useRef(null);
    const photoInputRef = useRef(null);
    const pollIntervalRef = useRef(null);

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            try {
                const [appRes, catRes, countryList] = await Promise.all([
                    getVendorApplicationStatus().catch(() => null),
                    getCategories().catch(() => []),
                    getActiveCountries().catch(() => [])
                ]);

                let vendorCountry = 'SA';
                if (appRes && appRes.data) {
                    setApplication(appRes.data);
                    if (appRes.data.status !== 'APPROVED') {
                        navigate('/vendor/apply');
                        return;
                    }
                    if (appRes.data.countryCode) {
                        vendorCountry = appRes.data.countryCode.toUpperCase();
                    }
                } else {
                    navigate('/vendor/apply');
                    return;
                }

                if (Array.isArray(catRes)) {
                    setCategories(catRes);
                    if (catRes.length > 0) {
                        setFormData(prev => ({ ...prev, categoryId: catRes[0].id, countryCode: vendorCountry }));
                    }
                } else {
                    setFormData(prev => ({ ...prev, countryCode: vendorCountry }));
                }
                if (Array.isArray(countryList)) setCountries(countryList);
            } catch (err) {
                console.error('Failed to init publisher studio', err);
            } finally {
                setLoading(false);
            }
        };

        init();

        return () => {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        };
    }, [navigate]);

    // ── Video Direct File Upload & Multi-Bitrate HLS Pipeline ──────
    const handleVideoFileSelect = async (file) => {
        if (!file) return;

        // Validation: 200MB max limit
        const MAX_SIZE = 200 * 1024 * 1024;
        if (file.size > MAX_SIZE) {
            alert(rtl 
                ? 'حجم الفيديو يتجاوز الحد المسموح به (الحد الأقصى 200 ميجابايت).' 
                : 'Video file size exceeds the 200MB limit.');
            return;
        }

        const validTypes = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-matroska'];
        if (!validTypes.includes(file.type) && !file.name.match(/\.(mp4|mov|webm|mkv)$/i)) {
            alert(rtl 
                ? 'يرجى اختيار ملف فيديو صالح بصيغة (MP4, MOV, WebM, MKV).' 
                : 'Please select a valid video file (MP4, MOV, WebM, MKV).');
            return;
        }

        setVideoFile(file);
        setVideoUploading(true);
        setUploadProgress(0);
        setJobProgress(0);
        setJobStatus('UPLOADING');
        setJobStepText(rtl ? 'جارٍ رفع الفيديو المصدري إلى خوادم LUXVONO...' : 'Uploading source video to LUXVONO servers...');
        setJobError('');

        try {
            // Step 1: Upload via product-service
            const uploadRes = await uploadMedia([file], (progressEvent) => {
                if (progressEvent.total) {
                    const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(percent);
                }
            }, 'ADAPTIVE_HLS');

            const result = Array.isArray(uploadRes) ? uploadRes[0] : (uploadRes?.data?.[0] || uploadRes);
            if (!result || !result.jobId) {
                // If it directly returned a URL (e.g. faststart or direct)
                if (result?.url) {
                    setFormData(prev => ({ ...prev, videoUrl: result.url }));
                    setJobStatus('COMPLETED');
                    setJobStepText(rtl ? 'تم تجهيز الفيديو بنجاح!' : 'Video ready!');
                    setVideoUploading(false);
                    return;
                }
                throw new Error(rtl ? 'فشل بدء مهمة معالجة الفيديو' : 'Failed to start video processing job');
            }

            const jobId = result.jobId;
            setJobStatus('ANALYZING');
            setJobStepText(rtl ? 'تحليل معدل البث ودقة الفيديو (FFmpeg Probing)...' : 'Probing video streams & metadata...');

            // Step 2: Poll FFmpeg HLS Transcoding Pipeline
            let attempts = 0;
            const maxAttempts = 120; // 3 minutes timeout

            pollIntervalRef.current = setInterval(async () => {
                attempts++;
                try {
                    const job = await getJobStatus(jobId);
                    if (!job) return;

                    const status = job.status;
                    const progress = job.progressPercentage || 0;
                    setJobProgress(progress);

                    if (status === 'ANALYZING') {
                        setJobStatus('ANALYZING');
                        setJobStepText(rtl ? 'تحليل معدل البث ودقة الفيديو (FFmpeg Probing)...' : 'Probing video streams & metadata...');
                    } else if (status === 'CONVERTING') {
                        setJobStatus('CONVERTING');
                        setJobStepText(rtl 
                            ? `جاري الترميز التكيفي FFmpeg HLS متعدد الجودات (1080p, 720p, 480p) [${progress}%]...` 
                            : `Adaptive FFmpeg HLS multi-bitrate encoding (1080p, 720p, 480p) [${progress}%]...`);
                    } else if (status === 'GENERATING_THUMBNAILS') {
                        setJobStatus('GENERATING_THUMBNAILS');
                        setJobStepText(rtl ? 'استخراج الغلاف والبوستر الذكي للفيديو...' : 'Extracting high-res video poster & thumbnail...');
                    } else if (status === 'COMPLETED') {
                        clearInterval(pollIntervalRef.current);
                        setJobStatus('COMPLETED');
                        setJobProgress(100);
                        setJobStepText(rtl ? '✨ تم تجهيز الفيديو والبث التكيفي HLS بنجاح فائقة الدقة!' : '✨ Video ready with adaptive HLS multi-bitrate streaming!');
                        setVideoUploading(false);

                        if (job.videoUrl) {
                            setFormData(prev => {
                                const updated = { ...prev, videoUrl: job.videoUrl };
                                // If no images yet and job extracted a thumbnail, suggest it as cover
                                if ((!prev.images || prev.images.length === 0 || !prev.images[0]) && job.thumbnailUrl) {
                                    updated.images = [job.thumbnailUrl];
                                }
                                return updated;
                            });
                        }
                    } else if (status === 'FAILED') {
                        clearInterval(pollIntervalRef.current);
                        setJobStatus('FAILED');
                        setJobError(job.errorMessage || (rtl ? 'فشلت معالجة الفيديو بواسطة FFmpeg' : 'FFmpeg video transcoding failed'));
                        setVideoUploading(false);
                    }

                    if (attempts >= maxAttempts) {
                        clearInterval(pollIntervalRef.current);
                        setJobStatus('FAILED');
                        setJobError(rtl ? 'انتهت مهلة معالجة الفيديو' : 'Video processing timed out');
                        setVideoUploading(false);
                    }
                } catch (pollErr) {
                    console.error('Error polling video job:', pollErr);
                }
            }, 1800);

        } catch (err) {
            console.error('Video upload error:', err);
            setJobStatus('FAILED');
            setJobError(err.response?.data?.message || err.message || (rtl ? 'فشل رفع الفيديو' : 'Upload failed'));
            setVideoUploading(false);
        }
    };

    // ── Direct Multiple Photos Upload ──────────────────────────────
    const handlePhotoFilesSelect = async (files) => {
        if (!files || files.length === 0) return;
        const fileList = Array.from(files);

        // Filter valid images
        const validImages = fileList.filter(f => f.type.startsWith('image/'));
        if (validImages.length === 0) {
            alert(rtl ? 'يرجى اختيار ملفات صور فقط (JPG, PNG, WebP).' : 'Please select image files (JPG, PNG, WebP).');
            return;
        }

        setPhotoUploading(true);
        setPhotoUploadProgress(0);

        try {
            const res = await uploadMedia(validImages, (progressEvent) => {
                if (progressEvent.total) {
                    const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setPhotoUploadProgress(percent);
                }
            }, 'ORIGINAL');

            const uploadedItems = Array.isArray(res) ? res : (res?.data || []);
            const newUrls = uploadedItems.filter(item => !item.isVideo && item.url).map(item => item.url);

            if (newUrls.length > 0) {
                setFormData(prev => {
                    // Remove blank slots first
                    const currentClean = prev.images.filter(img => img && img.trim().length > 0);
                    const combined = [...currentClean, ...newUrls].slice(0, 8);
                    return { ...prev, images: combined };
                });
            }
        } catch (err) {
            console.error('Photo upload error:', err);
            alert(rtl ? 'فشل رفع بعض الصور' : 'Failed to upload images');
        } finally {
            setPhotoUploading(false);
        }
    };

    // Handle Manual Image Array
    const handleAddImageSlot = () => {
        if (formData.images.length < 8) {
            setFormData(prev => ({ ...prev, images: [...prev.images, ''] }));
        }
    };

    const handleImageChange = (index, val) => {
        const updated = [...formData.images];
        updated[index] = val;
        setFormData(prev => ({ ...prev, images: updated }));
    };

    const handleRemoveImage = (index) => {
        if (formData.images.length > 1) {
            const updated = formData.images.filter((_, i) => i !== index);
            setFormData(prev => ({ ...prev, images: updated }));
        } else {
            setFormData(prev => ({ ...prev, images: [''] }));
        }
    };

    const handleMoveImage = (index, direction) => {
        const newImages = [...formData.images];
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= newImages.length) return;
        const temp = newImages[index];
        newImages[index] = newImages[targetIndex];
        newImages[targetIndex] = temp;
        setFormData(prev => ({ ...prev, images: newImages }));
    };

    // AI Copywriting Assistant Simulation
    const handleAiEnhance = async () => {
        if (!formData.name && !formData.nameAr) {
            alert(rtl ? 'يرجى كتابة عنوان أو فكرة للمنتج أولاً ليعمل الذكاء الاصطناعي.' : 'Please enter a product title or keyword first.');
            return;
        }

        setAiGenerating(true);
        setTimeout(() => {
            const baseKeyword = formData.nameAr || formData.name;
            const categoryObj = categories.find(c => String(c.id) === String(formData.categoryId));
            const categoryName = categoryObj ? (categoryObj.nameAr || categoryObj.name) : 'الفاخرة';

            setFormData(prev => ({
                ...prev,
                nameAr: prev.nameAr || `${baseKeyword} - الإصدار الملكي الحصري`,
                name: prev.name || `Imperial Edition: ${baseKeyword}`,
                descriptionAr: `✨ قطعة استثنائية من فئة ${categoryName} تجمع بين الحرفية العالية والأصالة الملكية. تم انتقاؤها بعناية لتلبي أرقى معايير الجودة والفخامة مع ضمان التميز الكامل.`,
                description: `✨ An exceptional luxury piece curated to the highest craftsmanship standards. Authentic and verified for distinguished clientele.`
            }));
            setAiGenerating(false);
        }, 1000);
    };

    const handlePublish = async (e) => {
        e.preventDefault();
        if (!formData.price || parseFloat(formData.price) <= 0) {
            alert(rtl ? 'يرجى إدخال سعر صحيح للمنتج.' : 'Please enter a valid price.');
            return;
        }

        const validImages = formData.images.filter(img => img && img.trim().length > 0);
        if (validImages.length === 0) {
            alert(rtl ? 'يرجى إرفاق صورة واحدة على الأقل للمنتج.' : 'Please add at least one product image.');
            return;
        }

        const targetCountry = (formData.countryCode || application?.countryCode || 'SA').toUpperCase();

        setSubmitting(true);
        try {
            const payload = {
                name: formData.name || formData.nameAr,
                nameAr: formData.nameAr,
                nameEn: formData.name,
                description: formData.description || formData.descriptionAr,
                descriptionAr: formData.descriptionAr,
                descriptionEn: formData.description,
                price: parseFloat(formData.price),
                compareAtPrice: formData.compareAtPrice ? parseFloat(formData.compareAtPrice) : null,
                quantity: parseInt(formData.quantity) || 1,
                sku: formData.sku,
                categoryId: Number(formData.categoryId) || (categories[0]?.id || 1),
                images: validImages,
                videoUrl: formData.videoUrl ? formData.videoUrl.trim() : null,
                countryCode: targetCountry,
                availableCountries: [targetCountry]
            };

            await createProduct(payload);
            alert(rtl 
                ? `🎉 تم رفع المنتج بنجاح! هو الآن قيد مراجعة الإدارة وسيظهر للمتسوقين في دولة (${targetCountry}) فور اعتماده مع مقطع الفيديو الترويجي.` 
                : `🎉 Product submitted successfully! It is now pending admin review and will be visible to shoppers in (${targetCountry}) once approved.`
            );
            navigate('/vendor/dashboard');
        } catch (err) {
            alert(err.response?.data?.message || (rtl ? 'فشل نشر المنتج' : 'Failed to publish product'));
        } finally {
            setSubmitting(false);
        }
    };

    const vendorCountryName = application?.countryCode || 'SA';

    if (loading) {
        return (
            <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0B0F19' }}>
                <div style={{ width: '45px', height: '45px', border: '3px solid rgba(212,175,55,0.2)', borderTopColor: '#d4af37', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh', background: '#0B0F19', color: '#fff', padding: '40px 20px', direction: rtl ? 'rtl' : 'ltr' }}>
            <div style={{ maxWidth: '1440px', margin: '0 auto' }}>
                
                {/* TOP HEADER */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px', flexWrap: 'wrap', gap: '16px' }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#d4af37', fontSize: '13px', fontWeight: '800', marginBottom: '6px' }}>
                            <Sparkles size={16} />
                            <span>LUXVONO VENDOR PUBLISHING STUDIO</span>
                        </div>
                        <h1 style={{ fontSize: '28px', fontWeight: '900', margin: 0 }}>
                            {rtl ? 'استوديو النشر الاحترافي ومعالجة الفيديو HLS' : 'Professional Vendor Publishing & HLS Studio'}
                        </h1>
                        <p style={{ color: '#9ca3af', fontSize: '14px', marginTop: '6px', margin: 0 }}>
                            {rtl 
                                ? `ارفع منتجاتك ومقاطع الفيديو الترويجية ليتم معالجتها آلياً وتوزيعها عبر البث التكيفي للمتسوقين في دولة (${vendorCountryName}).` 
                                : `Publish products with auto-transcoded adaptive HLS promotional videos for verified customer discovery in (${vendorCountryName}).`}
                        </p>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                            background: 'rgba(212, 175, 55, 0.15)', border: '1px solid rgba(212, 175, 55, 0.4)',
                            color: '#d4af37', padding: '8px 16px', borderRadius: '12px', fontSize: '13px', fontWeight: '800',
                            display: 'flex', alignItems: 'center', gap: '8px'
                        }}>
                            <Globe size={16} />
                            <span>{rtl ? `النطاق الجغرافي: ${vendorCountryName}` : `Target Geo: ${vendorCountryName}`}</span>
                        </div>

                        <button
                            onClick={() => navigate('/vendor/dashboard')}
                            style={{
                                background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)',
                                padding: '10px 18px', borderRadius: '12px', cursor: 'pointer', fontSize: '14px', fontWeight: '600'
                            }}
                        >
                            {rtl ? 'العودة للمنتجات' : 'Back to Dashboard'}
                        </button>
                    </div>
                </div>

                {/* MAIN GRID: 2 COLUMNS (FORM + REALTIME PREVIEW) */}
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.35fr) minmax(0, 1fr)', gap: '32px', alignItems: 'start' }}>
                    
                    {/* LEFT COLUMN: PUBLISHING FORM */}
                    <form onSubmit={handlePublish} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        
                        {/* 1. MEDIA STUDIO (VIDEO HLS + HD PHOTOS) */}
                        <div style={{ background: '#111827', border: '1px solid rgba(212, 175, 55, 0.25)', borderRadius: '24px', padding: '28px', boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#d4af37' }}>
                                    <div style={{ background: 'rgba(212,175,55,0.15)', padding: '8px', borderRadius: '10px', display: 'flex' }}>
                                        <Film size={22} color="#d4af37" />
                                    </div>
                                    <div>
                                        <h3 style={{ fontSize: '18px', fontWeight: '800', margin: 0 }}>
                                            {rtl ? '1. استوديو الفيديو الاحترافي (HLS Transcoding)' : '1. Professional Video Studio (HLS Transcoding)'}
                                        </h3>
                                        <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
                                            {rtl ? 'معالجة FFmpeg تلقائية متعددة الجودات (1080p, 720p, 480p) لتشغيل سلس بدون تقطيع' : 'Automated multi-bitrate FFmpeg encoding for buffer-free playback'}
                                        </div>
                                    </div>
                                </div>

                                {/* Mode Switcher Tabs */}
                                <div style={{ display: 'flex', background: 'rgba(255,255,255,0.06)', borderRadius: '10px', padding: '3px' }}>
                                    <button
                                        type="button"
                                        onClick={() => setVideoMode('UPLOAD')}
                                        style={{
                                            background: videoMode === 'UPLOAD' ? '#d4af37' : 'transparent',
                                            color: videoMode === 'UPLOAD' ? '#000' : '#9ca3af',
                                            border: 'none', padding: '6px 14px', borderRadius: '8px', fontSize: '12px',
                                            fontWeight: '800', cursor: 'pointer', transition: 'all 0.2s'
                                        }}
                                    >
                                        {rtl ? '📁 رفع فيديو ومعالجة' : '📁 Direct Upload'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setVideoMode('URL')}
                                        style={{
                                            background: videoMode === 'URL' ? '#d4af37' : 'transparent',
                                            color: videoMode === 'URL' ? '#000' : '#9ca3af',
                                            border: 'none', padding: '6px 14px', borderRadius: '8px', fontSize: '12px',
                                            fontWeight: '800', cursor: 'pointer', transition: 'all 0.2s'
                                        }}
                                    >
                                        {rtl ? '🔗 رابط مباشر' : '🔗 URL Link'}
                                    </button>
                                </div>
                            </div>

                            {/* TAB A: DIRECT VIDEO UPLOAD & FFMPEG MONITOR */}
                            {videoMode === 'UPLOAD' && (
                                <div style={{ marginBottom: '28px' }}>
                                    <input 
                                        type="file"
                                        ref={videoInputRef}
                                        accept="video/mp4,video/quicktime,video/webm,video/x-matroska"
                                        onChange={(e) => handleVideoFileSelect(e.target.files?.[0])}
                                        style={{ display: 'none' }}
                                    />

                                    {/* Dropzone Container */}
                                    {!formData.videoUrl && !videoUploading ? (
                                        <div 
                                            onDragOver={(e) => { e.preventDefault(); setDragOverVideo(true); }}
                                            onDragLeave={() => setDragOverVideo(false)}
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                setDragOverVideo(false);
                                                handleVideoFileSelect(e.dataTransfer.files?.[0]);
                                            }}
                                            onClick={() => videoInputRef.current?.click()}
                                            style={{
                                                border: dragOverVideo ? '2px dashed #d4af37' : '2px dashed rgba(212,175,55,0.35)',
                                                background: dragOverVideo ? 'rgba(212,175,55,0.08)' : 'rgba(0,0,0,0.3)',
                                                borderRadius: '16px', padding: '36px 20px', textAlign: 'center',
                                                cursor: 'pointer', transition: 'all 0.2s ease', position: 'relative'
                                            }}
                                        >
                                            <div style={{
                                                width: '56px', height: '56px', borderRadius: '50%', background: 'rgba(212,175,55,0.12)',
                                                color: '#d4af37', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                margin: '0 auto 16px auto', border: '1px solid rgba(212,175,55,0.3)'
                                            }}>
                                                <UploadCloud size={28} />
                                            </div>
                                            <div style={{ fontSize: '15px', fontWeight: '800', color: '#fff', marginBottom: '6px' }}>
                                                {rtl ? 'اسحب وأفلت مقطع الفيديو هنا أو اضغط للاختيار' : 'Drag & drop your product video here or click to browse'}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '14px' }}>
                                                {rtl ? 'يدعم MP4, MOV, WebM حتى 200 ميجابايت • يتم الترميز تلقائياً إلى HLS (1080p, 720p, 480p)' : 'Supports MP4, MOV, WebM up to 200MB • Auto-transcoded to adaptive HLS'}
                                            </div>
                                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'rgba(212,175,55,0.15)', color: '#d4af37', padding: '6px 16px', borderRadius: '20px', fontSize: '12px', fontWeight: '800' }}>
                                                <Sparkles size={14} /> {rtl ? 'معالجة ذكية فورية' : 'Smart Transcoding Included'}
                                            </div>
                                        </div>
                                    ) : null}

                                    {/* Transcoding Progress Monitor Card */}
                                    {videoUploading && (
                                        <div style={{
                                            background: '#090d16', border: '1px solid rgba(212,175,55,0.4)',
                                            borderRadius: '16px', padding: '24px', boxShadow: '0 8px 30px rgba(0,0,0,0.6)'
                                        }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <div style={{
                                                        width: '36px', height: '36px', borderRadius: '50%',
                                                        background: 'rgba(212,175,55,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center'
                                                    }}>
                                                        <Cpu size={18} color="#d4af37" className="animate-spin" />
                                                    </div>
                                                    <div>
                                                        <div style={{ fontSize: '14px', fontWeight: '800', color: '#fff' }}>
                                                            {rtl ? 'محرك المعالجة FFmpeg HLS نشط' : 'FFmpeg HLS Transcoding Engine Active'}
                                                        </div>
                                                        <div style={{ fontSize: '12px', color: '#d4af37' }}>
                                                            {jobStepText}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div style={{ fontSize: '18px', fontWeight: '900', color: '#d4af37' }}>
                                                    {jobStatus === 'UPLOADING' ? `${uploadProgress}%` : `${jobProgress}%`}
                                                </div>
                                            </div>

                                            {/* Visual Progress Bar */}
                                            <div style={{ width: '100%', height: '8px', background: 'rgba(255,255,255,0.08)', borderRadius: '10px', overflow: 'hidden', marginBottom: '18px' }}>
                                                <div style={{
                                                    width: `${jobStatus === 'UPLOADING' ? uploadProgress : jobProgress}%`,
                                                    height: '100%',
                                                    background: 'linear-gradient(90deg, #d4af37 0%, #f59e0b 50%, #10b981 100%)',
                                                    transition: 'width 0.3s ease',
                                                    borderRadius: '10px'
                                                }} />
                                            </div>

                                            {/* 4 Multi-Stage Pipeline Badges */}
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                                                {[
                                                    { id: '1', title: rtl ? 'الرفع السحابي' : 'Cloud Upload', active: jobStatus === 'UPLOADING', done: ['ANALYZING', 'CONVERTING', 'GENERATING_THUMBNAILS', 'COMPLETED'].includes(jobStatus) },
                                                    { id: '2', title: rtl ? 'فحص الترميز' : 'Probing', active: jobStatus === 'ANALYZING', done: ['CONVERTING', 'GENERATING_THUMBNAILS', 'COMPLETED'].includes(jobStatus) },
                                                    { id: '3', title: rtl ? 'تشفير HLS' : 'HLS Encoding', active: jobStatus === 'CONVERTING', done: ['GENERATING_THUMBNAILS', 'COMPLETED'].includes(jobStatus) },
                                                    { id: '4', title: rtl ? 'الغلاف والجاهزية' : 'Thumb & Ready', active: jobStatus === 'GENERATING_THUMBNAILS', done: jobStatus === 'COMPLETED' },
                                                ].map(stage => (
                                                    <div key={stage.id} style={{
                                                        background: stage.done ? 'rgba(16,185,129,0.12)' : stage.active ? 'rgba(212,175,55,0.15)' : 'rgba(255,255,255,0.03)',
                                                        border: stage.done ? '1px solid rgba(16,185,129,0.4)' : stage.active ? '1px solid rgba(212,175,55,0.5)' : '1px solid rgba(255,255,255,0.06)',
                                                        padding: '8px', borderRadius: '10px', textAlign: 'center', fontSize: '11px',
                                                        color: stage.done ? '#10b981' : stage.active ? '#d4af37' : '#6b7280',
                                                        fontWeight: '700'
                                                    }}>
                                                        <div>{stage.done ? '✓' : stage.active ? '●' : stage.id}</div>
                                                        <div style={{ marginTop: '2px' }}>{stage.title}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Video Successfully Ready Card */}
                                    {formData.videoUrl && !videoUploading && (
                                        <div style={{
                                            background: '#090d16', border: '1px solid rgba(16,185,129,0.4)',
                                            borderRadius: '16px', padding: '16px 20px', display: 'flex',
                                            justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px'
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <div style={{ background: 'rgba(16,185,129,0.15)', color: '#10b981', padding: '10px', borderRadius: '12px' }}>
                                                    <CheckCircle2 size={22} />
                                                </div>
                                                <div>
                                                    <div style={{ fontSize: '14px', fontWeight: '800', color: '#fff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <span>{rtl ? 'تم تجهيز وتوليد فيديو HLS بنجاح' : 'Adaptive HLS Video Ready'}</span>
                                                        <span style={{ background: '#10b981', color: '#000', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: '900' }}>
                                                            1080p / 720p / 480p
                                                        </span>
                                                    </div>
                                                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px', wordBreak: 'break-all' }}>
                                                        {formData.videoUrl}
                                                    </div>
                                                </div>
                                            </div>

                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button
                                                    type="button"
                                                    onClick={() => videoInputRef.current?.click()}
                                                    style={{
                                                        background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)',
                                                        padding: '8px 14px', borderRadius: '10px', fontSize: '12px', fontWeight: '700', cursor: 'pointer'
                                                    }}
                                                >
                                                    {rtl ? 'استبدال الفيديو' : 'Replace Video'}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setFormData(prev => ({ ...prev, videoUrl: '' }));
                                                        setVideoFile(null);
                                                        setJobStatus('IDLE');
                                                    }}
                                                    style={{
                                                        background: 'rgba(239,68,68,0.15)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)',
                                                        padding: '8px 14px', borderRadius: '10px', fontSize: '12px', fontWeight: '700', cursor: 'pointer'
                                                    }}
                                                >
                                                    {rtl ? 'حذف' : 'Remove'}
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Job Error Warning */}
                                    {jobError && (
                                        <div style={{
                                            marginTop: '12px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)',
                                            borderRadius: '12px', padding: '12px 16px', color: '#f87171', fontSize: '13px',
                                            display: 'flex', alignItems: 'center', gap: '8px'
                                        }}>
                                            <AlertCircle size={18} />
                                            <span>{jobError}</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* TAB B: DIRECT URL INPUT */}
                            {videoMode === 'URL' && (
                                <div style={{ marginBottom: '28px' }}>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#e5e7eb', marginBottom: '8px' }}>
                                        {rtl ? 'رابط مقطع الفيديو الترويجي للمنتج (Reel / Video URL)' : 'Promotional Video URL (Reel)'}
                                    </label>
                                    <input 
                                        type="url"
                                        value={formData.videoUrl}
                                        onChange={(e) => setFormData({ ...formData, videoUrl: e.target.value })}
                                        placeholder="https://... mp4, m3u8, video streaming link"
                                        style={{
                                            width: '100%', padding: '14px 16px', background: 'rgba(0,0,0,0.4)',
                                            border: '1px solid rgba(212, 175, 55, 0.4)', borderRadius: '12px', color: '#fff', fontSize: '14px'
                                        }}
                                    />
                                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Info size={14} color="#d4af37" />
                                        <span>{rtl ? 'يدعم روابط HLS المباشرة (.m3u8) وروابط MP4.' : 'Supports direct HLS (.m3u8) streams and MP4 links.'}</span>
                                    </div>
                                </div>
                            )}

                            {/* ── MULTI-PHOTO GALLERY WITH DIRECT FILE UPLOAD ── */}
                            <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '24px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
                                    <label style={{ fontSize: '15px', fontWeight: '800', color: '#fff', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <ImageIcon size={18} color="#d4af37" />
                                        <span>{rtl ? 'صور المنتج عالية الدقة (الرئيسية والإضافية)' : 'Product HD Photos'}</span>
                                    </label>

                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <input 
                                            type="file" 
                                            ref={photoInputRef}
                                            multiple 
                                            accept="image/*"
                                            onChange={(e) => handlePhotoFilesSelect(e.target.files)}
                                            style={{ display: 'none' }}
                                        />
                                        <button
                                            type="button"
                                            disabled={photoUploading}
                                            onClick={() => photoInputRef.current?.click()}
                                            style={{
                                                background: 'rgba(212,175,55,0.15)', border: '1px solid rgba(212,175,55,0.4)',
                                                color: '#d4af37', padding: '8px 16px', borderRadius: '10px', cursor: photoUploading ? 'not-allowed' : 'pointer',
                                                display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '800'
                                            }}
                                        >
                                            <Upload size={14} />
                                            {photoUploading ? (rtl ? `جاري الرفع ${photoUploadProgress}%` : `Uploading ${photoUploadProgress}%`) : (rtl ? 'رفع صور من الجهاز 📷' : 'Upload Device Photos 📷')}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleAddImageSlot}
                                            style={{
                                                background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)',
                                                color: '#fff', padding: '8px 14px', borderRadius: '10px', cursor: 'pointer',
                                                display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '700'
                                            }}
                                        >
                                            <Plus size={14} /> {rtl ? 'خانة رابط' : 'Add URL'}
                                        </button>
                                    </div>
                                </div>

                                {/* Drag and drop dropzone for photos */}
                                <div 
                                    onDragOver={(e) => { e.preventDefault(); setDragOverPhotos(true); }}
                                    onDragLeave={() => setDragOverPhotos(false)}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        setDragOverPhotos(false);
                                        handlePhotoFilesSelect(e.dataTransfer.files);
                                    }}
                                    style={{
                                        border: dragOverPhotos ? '2px dashed #d4af37' : '1px dashed rgba(255,255,255,0.15)',
                                        background: dragOverPhotos ? 'rgba(212,175,55,0.06)' : 'rgba(0,0,0,0.2)',
                                        borderRadius: '12px', padding: '16px', textAlign: 'center', marginBottom: '16px',
                                        cursor: 'pointer', fontSize: '12px', color: '#9ca3af'
                                    }}
                                    onClick={() => photoInputRef.current?.click()}
                                >
                                    {rtl ? '💡 اسحب صور متعددة وأفلتها هنا للرفع التلقائي السريع' : '💡 Drag and drop multiple photos here for instant batch upload'}
                                </div>

                                {/* Photo Rows List */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    {formData.images.map((img, idx) => (
                                        <div key={idx} style={{
                                            display: 'flex', gap: '10px', alignItems: 'center',
                                            background: 'rgba(0,0,0,0.3)', padding: '8px 12px', borderRadius: '12px',
                                            border: idx === 0 ? '1px solid rgba(212,175,55,0.4)' : '1px solid rgba(255,255,255,0.08)'
                                        }}>
                                            {/* Preview Thumbnail */}
                                            <div style={{
                                                width: '46px', height: '46px', borderRadius: '8px', overflow: 'hidden',
                                                background: '#000', border: '1px solid rgba(255,255,255,0.15)', flexShrink: 0
                                            }}>
                                                {img ? (
                                                    <img 
                                                        src={resolveStorefrontImageUrl(img)} 
                                                        alt={`Preview ${idx}`}
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        onError={(e) => { e.target.style.display = 'none'; }}
                                                    />
                                                ) : (
                                                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4b5563' }}>
                                                        <ImageIcon size={18} />
                                                    </div>
                                                )}
                                            </div>

                                            {/* Badge */}
                                            <span style={{
                                                background: idx === 0 ? '#d4af37' : 'rgba(255,255,255,0.08)',
                                                color: idx === 0 ? '#000' : '#d1d5db',
                                                padding: '6px 10px', borderRadius: '8px', fontSize: '11px', fontWeight: '800',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {idx === 0 ? (rtl ? '🌟 الغلاف' : '🌟 Cover') : `#${idx + 1}`}
                                            </span>

                                            {/* URL Input */}
                                            <input 
                                                type="url"
                                                required={idx === 0}
                                                value={img}
                                                onChange={(e) => handleImageChange(idx, e.target.value)}
                                                placeholder={idx === 0 ? (rtl ? 'رابط الصورة الرئيسية أو قم بالرفع أعلاه' : 'Cover image URL or upload above') : (rtl ? 'رابط صورة إضافية' : 'Extra photo URL')}
                                                style={{
                                                    flex: 1, padding: '10px 14px', background: 'rgba(0,0,0,0.5)',
                                                    border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: '13px'
                                                }}
                                            />

                                            {/* Reorder Buttons */}
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                <button
                                                    type="button"
                                                    disabled={idx === 0}
                                                    onClick={() => handleMoveImage(idx, -1)}
                                                    style={{ background: 'transparent', border: 'none', color: idx === 0 ? '#374151' : '#9ca3af', cursor: idx === 0 ? 'default' : 'pointer', padding: '2px' }}
                                                >
                                                    <ChevronUp size={14} />
                                                </button>
                                                <button
                                                    type="button"
                                                    disabled={idx === formData.images.length - 1}
                                                    onClick={() => handleMoveImage(idx, 1)}
                                                    style={{ background: 'transparent', border: 'none', color: idx === formData.images.length - 1 ? '#374151' : '#9ca3af', cursor: idx === formData.images.length - 1 ? 'default' : 'pointer', padding: '2px' }}
                                                >
                                                    <ChevronDown size={14} />
                                                </button>
                                            </div>

                                            {/* Remove Button */}
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveImage(idx)}
                                                style={{ background: 'rgba(239,68,68,0.15)', border: 'none', color: '#f87171', padding: '8px', borderRadius: '8px', cursor: 'pointer' }}
                                            >
                                                <Trash2 size={15} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* 2. PRODUCT DETAILS & AI COPYWRITER */}
                        <div style={{ background: '#111827', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '24px', padding: '28px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#d4af37' }}>
                                    <Tag size={20} />
                                    <h3 style={{ fontSize: '18px', fontWeight: '800', margin: 0 }}>
                                        {rtl ? '2. تفاصيل المنتج والذكاء الاصطناعي' : '2. Details & AI Copywriter'}
                                    </h3>
                                </div>

                                <button
                                    type="button"
                                    onClick={handleAiEnhance}
                                    disabled={aiGenerating}
                                    style={{
                                        background: 'linear-gradient(135deg, #d4af37 0%, #aa820a 100%)',
                                        color: '#000', border: 'none', padding: '8px 16px', borderRadius: '10px',
                                        fontWeight: '800', fontSize: '13px', cursor: aiGenerating ? 'not-allowed' : 'pointer',
                                        display: 'flex', alignItems: 'center', gap: '6px', boxShadow: '0 4px 14px rgba(212, 175, 55, 0.3)'
                                    }}
                                >
                                    <Sparkles size={16} />
                                    {aiGenerating ? (rtl ? 'جاري التحسين الذكي...' : 'Generating...') : (rtl ? 'تحسين بالذكاء الاصطناعي ✨' : 'AI Enhance Copy ✨')}
                                </button>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '6px' }}>
                                        {rtl ? 'عنوان المنتج بالعربي' : 'Arabic Title'} *
                                    </label>
                                    <input 
                                        type="text" required
                                        value={formData.nameAr}
                                        onChange={(e) => setFormData({ ...formData, nameAr: e.target.value })}
                                        placeholder="مثال: دهن العود الملكي المعتّق"
                                        style={{ width: '100%', padding: '12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '10px', color: '#fff', fontSize: '14px' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '6px' }}>
                                        {rtl ? 'عنوان المنتج بالإنجليزي (اختياري)' : 'English Title'}
                                    </label>
                                    <input 
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="e.g. Royal Aged Oud Oil"
                                        style={{ width: '100%', padding: '12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '10px', color: '#fff', fontSize: '14px' }}
                                    />
                                </div>
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '6px' }}>
                                    {rtl ? 'وصف المنتج وقصته الحصرية (بالعربي)' : 'Arabic Description'} *
                                </label>
                                <textarea 
                                    rows="4" required
                                    value={formData.descriptionAr}
                                    onChange={(e) => setFormData({ ...formData, descriptionAr: e.target.value })}
                                    placeholder="اكتب وصفاً جذاباً يوضح أصل القطعة ومميزاتها الحصرية..."
                                    style={{ width: '100%', padding: '12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '10px', color: '#fff', fontSize: '14px', lineHeight: '1.6' }}
                                />
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '6px' }}>
                                    {rtl ? 'الوصف بالإنجليزي (اختياري)' : 'English Description'}
                                </label>
                                <textarea 
                                    rows="3"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Luxury description for international VIP buyers..."
                                    style={{ width: '100%', padding: '12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '10px', color: '#fff', fontSize: '14px', lineHeight: '1.6' }}
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '6px' }}>
                                        {rtl ? 'تصنيف المنتج الفاخر' : 'Luxury Category'} *
                                    </label>
                                    <select 
                                        value={formData.categoryId}
                                        onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                                        style={{ width: '100%', padding: '12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '10px', color: '#fff', fontSize: '14px' }}
                                    >
                                        {categories.map(cat => (
                                            <option key={cat.id} value={cat.id}>
                                                {cat.nameAr || cat.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '6px' }}>
                                        {rtl ? 'رمز التخزين التعريفي (SKU)' : 'SKU Code'}
                                    </label>
                                    <input 
                                        type="text"
                                        value={formData.sku}
                                        onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                                        style={{ width: '100%', padding: '12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '10px', color: '#fff', fontSize: '14px' }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* 3. PRICING & INVENTORY */}
                        <div style={{ background: '#111827', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '24px', padding: '28px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#d4af37', marginBottom: '20px' }}>
                                <DollarSign size={20} />
                                <h3 style={{ fontSize: '18px', fontWeight: '800', margin: 0 }}>
                                    {rtl ? '3. التسعير والمخزون' : '3. Pricing & Inventory'}
                                </h3>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '16px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '6px' }}>
                                        {rtl ? 'السعر الفعلي (بالعملة المحلية)' : 'Price'} *
                                    </label>
                                    <input 
                                        type="number" step="0.01" required
                                        value={formData.price}
                                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                        placeholder="0.00"
                                        style={{ width: '100%', padding: '12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '10px', color: '#fff', fontSize: '14px' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '6px' }}>
                                        {rtl ? 'السعر قبل الخصم (اختياري)' : 'Compare At Price'}
                                    </label>
                                    <input 
                                        type="number" step="0.01"
                                        value={formData.compareAtPrice}
                                        onChange={(e) => setFormData({ ...formData, compareAtPrice: e.target.value })}
                                        placeholder="0.00"
                                        style={{ width: '100%', padding: '12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '10px', color: '#fff', fontSize: '14px' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '6px' }}>
                                        {rtl ? 'الكمية المتوفرة' : 'Available Stock'} *
                                    </label>
                                    <input 
                                        type="number" min="1" required
                                        value={formData.quantity}
                                        onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                                        style={{ width: '100%', padding: '12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '10px', color: '#fff', fontSize: '14px' }}
                                    />
                                </div>
                            </div>

                            <div style={{
                                background: 'rgba(212, 175, 55, 0.08)', border: '1px solid rgba(212, 175, 55, 0.2)',
                                borderRadius: '12px', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: '#e5e7eb'
                            }}>
                                <CheckCircle2 size={18} color="#d4af37" />
                                <span>
                                    {rtl 
                                        ? `تم ضبط المنتج ليظهر تلقائياً لعملاء: ${(countries.find(c => c.countryCode === (formData.countryCode || application?.countryCode || 'SA'))?.nativeName) || (formData.countryCode || application?.countryCode || 'SA')}` 
                                        : `Product visibility locked to shoppers in: ${formData.countryCode || application?.countryCode || 'SA'}`}
                                </span>
                            </div>
                        </div>

                        {/* SUBMIT BUTTON */}
                        <button
                            type="submit"
                            disabled={submitting || videoUploading}
                            style={{
                                background: (submitting || videoUploading) ? 'rgba(212,175,55,0.4)' : 'linear-gradient(135deg, #d4af37 0%, #b8860b 100%)',
                                color: '#000', border: 'none', padding: '18px', borderRadius: '16px',
                                fontSize: '17px', fontWeight: '900', cursor: (submitting || videoUploading) ? 'not-allowed' : 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                                boxShadow: '0 8px 24px rgba(212, 175, 55, 0.35)', transition: 'transform 0.15s ease'
                            }}
                        >
                            <ShieldCheck size={22} />
                            {submitting 
                                ? (rtl ? 'جاري رفع ونشر المنتج...' : 'Publishing...') 
                                : videoUploading
                                ? (rtl ? 'يرجى انتظار اكتمال معالجة الفيديو...' : 'Waiting for video transcoding...')
                                : (rtl ? 'إرسال المنشور للمراجعة والاعتماد الرسمي 🚀' : 'Submit for Admin Review & Publishing 🚀')}
                        </button>
                    </form>

                    {/* RIGHT COLUMN: INTERACTIVE LIVE PREVIEW STUDIO */}
                    <div style={{ position: 'sticky', top: '24px' }}>
                        <div style={{ background: '#111827', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '24px', padding: '24px' }}>
                            
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#d4af37', fontSize: '14px', fontWeight: '800' }}>
                                    <Eye size={18} />
                                    <span>{rtl ? 'المعاينة الحية الفورية' : 'Live Real-time Preview'}</span>
                                </div>

                                <div style={{ display: 'flex', gap: '6px' }}>
                                    <button
                                        type="button"
                                        onClick={() => setPreviewMode('mobile')}
                                        style={{
                                            background: previewMode === 'mobile' ? '#d4af37' : 'rgba(255,255,255,0.05)',
                                            color: previewMode === 'mobile' ? '#000' : '#fff',
                                            border: 'none', padding: '6px 12px', borderRadius: '8px', cursor: 'pointer',
                                            display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: '700'
                                        }}
                                    >
                                        <Smartphone size={14} /> {rtl ? 'تطبيق الجوال' : 'Mobile'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setPreviewMode('desktop')}
                                        style={{
                                            background: previewMode === 'desktop' ? '#d4af37' : 'rgba(255,255,255,0.05)',
                                            color: previewMode === 'desktop' ? '#000' : '#fff',
                                            border: 'none', padding: '6px 12px', borderRadius: '8px', cursor: 'pointer',
                                            display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: '700'
                                        }}
                                    >
                                        <Monitor size={14} /> {rtl ? 'المتجر' : 'Store'}
                                    </button>
                                </div>
                            </div>

                            {/* MOBILE PREVIEW SHELL */}
                            {previewMode === 'mobile' ? (
                                <div style={{
                                    width: '320px', height: '560px', margin: '0 auto', background: '#000',
                                    borderRadius: '36px', border: '8px solid #1f2937', position: 'relative',
                                    overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.8)'
                                }}>
                                    {/* Video / Photo Layer */}
                                    {formData.videoUrl ? (
                                        <HlsVideoPlayer 
                                            src={formData.videoUrl} 
                                            poster={formData.images[0]}
                                            autoPlay={true}
                                            loop={true}
                                            muted={true}
                                        />
                                    ) : (
                                        <img 
                                            src={resolveStorefrontImageUrl(formData.images[0]) || 'https://images.unsplash.com/photo-1592945403244-b3fbafd7f539?w=600'}
                                            alt="Preview"
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                    )}

                                    {/* Country Badge */}
                                    <div style={{
                                        position: 'absolute', top: '16px', right: '16px', zIndex: 10,
                                        background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(8px)',
                                        border: '1px solid rgba(212,175,55,0.5)', color: '#d4af37',
                                        padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '800'
                                    }}>
                                        {vendorCountryName} 🇸🇦
                                    </div>

                                    {/* Bottom Overlay Info */}
                                    <div style={{
                                        position: 'absolute', bottom: 0, insetInline: 0, zIndex: 10,
                                        background: 'linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.6) 60%, transparent 100%)',
                                        padding: '20px 16px', direction: rtl ? 'rtl' : 'ltr'
                                    }}>
                                        <div style={{ fontSize: '15px', fontWeight: '800', color: '#fff', marginBottom: '4px' }}>
                                            {formData.nameAr || formData.name || (rtl ? 'عنوان المنتج هنا' : 'Product Title')}
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#d1d5db', marginBottom: '10px', lineHeight: '1.4' }}>
                                            {(formData.descriptionAr || formData.description || (rtl ? 'وصف المنتج وتفاصيله...' : 'Description...')).slice(0, 75)}...
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span style={{ fontSize: '18px', fontWeight: '900', color: '#d4af37' }}>
                                                {formData.price ? `${formData.price} SAR` : '0.00 SAR'}
                                            </span>
                                            <span style={{
                                                background: '#d4af37', color: '#000', padding: '6px 14px',
                                                borderRadius: '20px', fontSize: '12px', fontWeight: '800'
                                            }}>
                                                {rtl ? 'طلب فوري' : 'Buy Now'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                /* DESKTOP PRODUCT CARD PREVIEW */
                                <div style={{ background: '#0B0F19', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', overflow: 'hidden' }}>
                                    <div style={{ height: '240px', position: 'relative', background: '#000' }}>
                                        {formData.videoUrl ? (
                                            <HlsVideoPlayer 
                                                src={formData.videoUrl} 
                                                poster={formData.images[0]}
                                                autoPlay={true}
                                                loop={true}
                                                muted={true}
                                            />
                                        ) : (
                                            <img 
                                                src={resolveStorefrontImageUrl(formData.images[0]) || 'https://images.unsplash.com/photo-1592945403244-b3fbafd7f539?w=600'}
                                                alt="Card Preview"
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                        )}
                                        {formData.videoUrl && (
                                            <div style={{
                                                position: 'absolute', top: '10px', right: '10px', zIndex: 10,
                                                background: 'rgba(0,0,0,0.7)', color: '#d4af37', padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '700'
                                            }}>
                                                🎥 HLS تفاعلي
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ padding: '16px', direction: rtl ? 'rtl' : 'ltr' }}>
                                        <div style={{ fontSize: '11px', color: '#d4af37', fontWeight: '700', marginBottom: '4px' }}>
                                            {categories.find(c => String(c.id) === String(formData.categoryId))?.name || 'Category'}
                                        </div>
                                        <h4 style={{ fontSize: '15px', fontWeight: '700', margin: '0 0 8px 0', color: '#fff' }}>
                                            {formData.nameAr || formData.name || 'عنوان المنتج'}
                                        </h4>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span style={{ fontSize: '16px', fontWeight: '800', color: '#d4af37' }}>
                                                {formData.price ? `${formData.price} SAR` : '0.00 SAR'}
                                            </span>
                                            <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                                                دولة: {vendorCountryName}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* WORKFLOW TRACKER */}
                            <div style={{ marginTop: '20px', background: 'rgba(255,255,255,0.03)', padding: '14px', borderRadius: '12px', fontSize: '12px', color: '#9ca3af' }}>
                                <div style={{ fontWeight: '700', color: '#fff', marginBottom: '6px' }}>
                                    {rtl ? '🛡️ مراحل اعتماد المنشور:' : '🛡️ Moderation Pipeline:'}
                                </div>
                                <div>1. رفع وحفظ المنشور بحالة (Pending).</div>
                                <div>2. فحص الفيديو والصور والبيانات من الإدارة.</div>
                                <div>3. النشر المباشر لمتسوقي {vendorCountryName} فور الاعتماد.</div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default VendorPublishProductPage;
